/**
 * ApiClientService
 *
 * Centralized service for all API endpoint definitions and request handling.
 * Provides type-safe API methods without React dependencies.
 */
import type {
  AcceptInvitationDto,
  AcceptInvitationResponseDto,
  AcceptTermsResponseDto,
  AccountDeletionCancelResponseDto,
  AccountDeletionRequestResponseDto,
  // Activity DTOs
  ActivityResponseDto,
  AddCommentDto,
  AddProGroupDto,
  // Admin DTOs
  AdminUserSearchResultDto,
  // App config DTOs
  AppConfigDto,
  AuthResponseDto,
  // Balance DTOs
  BalanceResponseDto,
  // Comment DTOs
  ClaimLineItemsDto,
  ClaimLinkDto,
  ClaimLinkViewDto,
  CommentResponseDto,
  ContentReportResponseDto,
  CreateContentReportDto,
  CreateExpenseDto,
  CreateGroupDto,
  CreateImportJobResponseDto,
  // Invitation DTOs
  CreateInvitationDto,
  // Placeholder member DTOs
  CreatePlaceholderDto,
  CreateReceiptDraftDto,
  CreateSettlementDto,
  CurrentUserDto,
  DescribeExpenseRequestDto,
  DescribeExpenseResponseDto,
  // Exchange rate DTOs
  ExchangeRatePreviewDto,
  // Expense DTOs
  ExpenseResponseDto,
  // Feature flag DTOs
  FeatureFlagsDto,
  GetUploadUrlDto,
  // Group DTOs
  GroupMemberResponseDto,
  GroupResponseDto,
  GroupWithDashboard,
  // Import DTOs
  ImportJobDto,
  InvitationResponseDto,
  // Auth DTOs
  LinkPickDto,
  LinkPickResultDto,
  LoginDto,
  // Notification DTOs
  NotificationSettingsResponseDto,
  OperationSuccessDto,
  // Common DTOs
  PaginatedResponseDto,
  PlaceholderClaimDto,
  PlaceholderClaimListDto,
  PredictCategoryRequestDto,
  PredictCategoryResponseDto,
  ProGroupMutationResponseDto,
  ProGroupsListResponseDto,
  ReceiptDraftResponseDto,
  ReceiptScanResultDto,
  RecurringRuleDto,
  RecurringRulesResponseDto,
  RegisterDto,
  ResolveSplitwiseLabelsRequestDto,
  ResolveSplitwiseLabelsResponseDto,
  ScanReceiptDraftDto,
  // Settlement DTOs
  SettlementResponseDto,
  SettlementStatus,
  ShadowTokenResponseDto,
  SimplifiedDebtsDto,
  SplitwiseImportRequestDto,
  StartShadowDto,
  SubscribeDeviceDto,
  SuccessResponseDto,
  UpdateCommentDto,
  UpdateExpenseDto,
  UpdateGroupDto,
  UpdateNotificationSettingsDto,
  UpdatePlaceholderDto,
  UpdateProfileDto,
  UpdateRecurringRuleDto,
  UpdateSettingsDto,
  UpdateSettlementDto,
  UploadUrlResponseDto,
  // User DTOs
  UserResponseDto,
  ValidateInvitationResponseDto,
} from '@splitfiesta/shared';
import {
  RECEIPT_SCAN_CLIENT_TIMEOUT_MS,
  VOICE_DESCRIBE_CLIENT_TIMEOUT_MS,
} from '@splitfiesta/shared';

/**
 * User net balance response structure
 */
export interface UserNetBalanceResponse {
  groupBalances: Record<
    string,
    { groupName: string; balances: Record<string, number> }
  >;
  totalsByCurrency: Record<string, number>;
}

/**
 * User settings response structure
 */
export interface UserSettingsResponse {
  language: string;
  timezone: string;
  currency: string;
  notifications: boolean;
  exampleGroupHidden?: boolean;
}

export type UploadPresignedUrlRequest = GetUploadUrlDto;

/** `headers` are signed into the URL: the PUT must send them as given. */
export type UploadPresignedUrlResponse = UploadUrlResponseDto;

/**
 * Upload confirmation request
 */
export interface UploadConfirmRequest {
  type: 'profile' | 'group' | 'expense';
  entityId: string;
  imageUrl: string;
  /** Full-size original image URL (for profile/group dual-upload) */
  imageUrlOriginal?: string;
}

/** Per-call transport options; each platform's request wrapper honours them. */
export interface ApiCallOptions {
  timeoutMs?: number;
}

/**
 * Base API interface for HTTP methods
 * Matches the interface from useApi hook
 */
interface BaseApi {
  get: <TResponse>(
    endpoint: string,
    options?: ApiCallOptions
  ) => Promise<TResponse | SuccessResponseDto<TResponse>>;
  post: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiCallOptions
  ) => Promise<TResponse | SuccessResponseDto<TResponse>>;
  put: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiCallOptions
  ) => Promise<TResponse | SuccessResponseDto<TResponse>>;
  patch: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiCallOptions
  ) => Promise<TResponse | SuccessResponseDto<TResponse>>;
  delete: <TResponse, TRequest = unknown>(
    endpoint: string,
    body?: TRequest,
    options?: ApiCallOptions
  ) => Promise<TResponse | SuccessResponseDto<TResponse>>;
}

/**
 * Query parameters for list endpoints
 */
interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Service class for API client operations
 */
export class ApiClientService {
  /**
   * Build query string from parameters
   */
  private static buildQueryString(
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    if (!params) return '';

    const queryString = new URLSearchParams(
      Object.entries(params)
        .filter(([_, v]) => v !== undefined)
        // Cast the tuple shape explicitly. Without it TS widens
        // `[k, String(v)]` to `string[]`, which leaves `string[][]`
        // — not assignable to `URLSearchParams`'s
        // `readonly [string, string][]` overload. Local builds
        // pass under looser inference; Docker has been observed
        // stricter (TS 5.9 + Linux toolchain), so make the type
        // explicit.
        .map(([k, v]) => [k, String(v)] as [string, string])
    ).toString();

    return queryString ? `?${queryString}` : '';
  }

  /**
   * Authentication endpoints
   */
  static auth = {
    login: (api: BaseApi, credentials: LoginDto) =>
      api.post<SuccessResponseDto<AuthResponseDto>, LoginDto>(
        '/auth/login',
        credentials
      ),

    register: (api: BaseApi, credentials: RegisterDto) =>
      api.post<SuccessResponseDto<AuthResponseDto>, RegisterDto>(
        '/auth/register',
        credentials
      ),

    refresh: (api: BaseApi, refreshToken: string) =>
      api.post<SuccessResponseDto<AuthResponseDto>, { refreshToken: string }>(
        '/auth/refresh',
        { refreshToken }
      ),

    me: (api: BaseApi) =>
      api.get<SuccessResponseDto<CurrentUserDto>>('/auth/me'),
  };

  /**
   * App config endpoints (public). Drives the mobile app-version gate.
   */
  static config = {
    get: (api: BaseApi) => api.get<SuccessResponseDto<AppConfigDto>>('/config'),
  };

  /**
   * Feature flags (auth). Resolved server-side per user; the client only
   * ever sees booleans.
   */
  static featureFlags = {
    get: (api: BaseApi) =>
      api.get<SuccessResponseDto<FeatureFlagsDto>>('/feature-flags'),
  };

  /**
   * Pro groups (auth). DELETE passes `{}` explicitly; both transports send
   * `{}` for a missing non-GET body anyway, never an empty JSON body (a 400).
   */
  static pro = {
    addGroup: (api: BaseApi, body: AddProGroupDto) =>
      api.post<SuccessResponseDto<ProGroupMutationResponseDto>, AddProGroupDto>(
        '/pro/groups',
        body
      ),

    removeGroup: (api: BaseApi, groupId: string) =>
      api.delete<
        SuccessResponseDto<ProGroupMutationResponseDto>,
        Record<string, never>
      >(`/pro/groups/${groupId}`, {}),

    listGroups: (api: BaseApi) =>
      api.get<SuccessResponseDto<ProGroupsListResponseDto>>('/pro/groups'),
  };

  /** Recurring rules of a group (auth, `pro` flag). */
  static recurringRules = {
    list: (api: BaseApi, groupId: string) =>
      api.get<RecurringRulesResponseDto>(`/groups/${groupId}/recurring-rules`),

    updateStatus: (
      api: BaseApi,
      groupId: string,
      ruleId: string,
      body: UpdateRecurringRuleDto
    ) =>
      api.patch<SuccessResponseDto<RecurringRuleDto>, UpdateRecurringRuleDto>(
        `/groups/${groupId}/recurring-rules/${ruleId}`,
        body
      ),

    stop: (api: BaseApi, groupId: string, ruleId: string) =>
      api.delete<SuccessResponseDto<{ deleted: true }>, Record<string, never>>(
        `/groups/${groupId}/recurring-rules/${ruleId}`,
        {}
      ),
  };

  /** Receipt drafts and scans (auth, `pro` flag). The scan outlives the 15 s default. */
  static receipts = {
    createDraft: (api: BaseApi, groupId: string, body: CreateReceiptDraftDto) =>
      api.post<
        SuccessResponseDto<ReceiptDraftResponseDto>,
        CreateReceiptDraftDto
      >(`/groups/${groupId}/receipt-drafts`, body),

    scanDraft: (
      api: BaseApi,
      groupId: string,
      draftId: string,
      body: ScanReceiptDraftDto
    ) =>
      api.post<SuccessResponseDto<ReceiptScanResultDto>, ScanReceiptDraftDto>(
        `/groups/${groupId}/receipt-drafts/${draftId}/scan`,
        body,
        { timeoutMs: RECEIPT_SCAN_CLIENT_TIMEOUT_MS }
      ),
  };

  /**
   * User endpoints
   */
  static user = {
    getProfile: (api: BaseApi) =>
      api.get<SuccessResponseDto<UserResponseDto>>('/user/profile'),

    updateProfile: (api: BaseApi, data: UpdateProfileDto) =>
      api.put<SuccessResponseDto<UserResponseDto>, UpdateProfileDto>(
        '/user/profile',
        data
      ),

    getSettings: (api: BaseApi) =>
      api.get<SuccessResponseDto<UserSettingsResponse>>('/user/settings'),

    updateSettings: (api: BaseApi, data: UpdateSettingsDto) =>
      api.put<SuccessResponseDto<UserSettingsResponse>, UpdateSettingsDto>(
        '/user/settings',
        data
      ),

    acceptTerms: (api: BaseApi) =>
      api.post<SuccessResponseDto<AcceptTermsResponseDto>, object>(
        '/user/accept-terms',
        {}
      ),

    /**
     * Emails the account a single-use deletion-confirmation link (1h TTL).
     * lang: the client UI's active language — the server uses it as the
     * email-language fallback for accounts without an explicit setting.
     */
    requestDeletion: (api: BaseApi, lang?: string) =>
      api.post<SuccessResponseDto<AccountDeletionRequestResponseDto>, object>(
        '/user/deletion-request',
        lang ? { lang } : {}
      ),

    /** Explicitly cancels a scheduled deletion during the grace window. */
    cancelDeletion: (api: BaseApi) =>
      api.post<SuccessResponseDto<AccountDeletionCancelResponseDto>, object>(
        '/user/deletion-cancel',
        {}
      ),

    updatePassword: (
      api: BaseApi,
      data: { currentPassword: string; newPassword: string }
    ) =>
      api.put<
        SuccessResponseDto<void>,
        { currentPassword: string; newPassword: string }
      >('/user/password', data),
  };

  /**
   * Moderation endpoints (store compliance — Apple 1.2 / Play UGC policy)
   */
  static moderation = {
    reportContent: (api: BaseApi, data: CreateContentReportDto) =>
      api.post<
        SuccessResponseDto<ContentReportResponseDto>,
        CreateContentReportDto
      >('/content-reports', data),
  };

  /**
   * Group endpoints
   */
  static groups = {
    list: (api: BaseApi, params?: ListParams) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<
        SuccessResponseDto<PaginatedResponseDto<GroupResponseDto>>
      >(`/groups${queryString}`);
    },

    listWithBalances: (api: BaseApi, params?: { search?: string }) => {
      const queryString = params?.search
        ? `?search=${encodeURIComponent(params.search)}`
        : '';
      return api.get<SuccessResponseDto<GroupResponseDto[]>>(
        `/groups/with-balances${queryString}`
      );
    },

    getById: (api: BaseApi, id: string, includeBalance = false) =>
      api.get<SuccessResponseDto<GroupWithDashboard>>(
        `/groups/${id}${includeBalance ? '?include=dashboard' : ''}`
      ),

    create: (api: BaseApi, data: CreateGroupDto) =>
      api.post<SuccessResponseDto<GroupResponseDto>, CreateGroupDto>(
        '/groups',
        data
      ),

    update: (api: BaseApi, id: string, data: UpdateGroupDto) =>
      api.put<SuccessResponseDto<GroupResponseDto>, UpdateGroupDto>(
        `/groups/${id}`,
        data
      ),

    delete: (api: BaseApi, id: string) =>
      api.delete<SuccessResponseDto<void>>(`/groups/${id}`),

    removeMember: (api: BaseApi, groupId: string, memberId: string) =>
      api.delete<SuccessResponseDto<void>>(
        `/groups/${groupId}/members/${memberId}`
      ),

    updateMemberRole: (
      api: BaseApi,
      groupId: string,
      memberId: string,
      role: 'admin' | 'member'
    ) =>
      api.put<SuccessResponseDto<void>, { role: 'admin' | 'member' }>(
        `/groups/${groupId}/members/${memberId}/role`,
        { role }
      ),

    createPlaceholder: (api: BaseApi, groupId: string, name: string) =>
      api.post<
        SuccessResponseDto<GroupMemberResponseDto>,
        CreatePlaceholderDto
      >(`/groups/${groupId}/placeholders`, { name }),

    updatePlaceholder: (
      api: BaseApi,
      groupId: string,
      userId: string,
      name: string
    ) =>
      api.put<SuccessResponseDto<GroupMemberResponseDto>, UpdatePlaceholderDto>(
        `/groups/${groupId}/placeholders/${userId}`,
        { name }
      ),

    deletePlaceholder: (api: BaseApi, groupId: string, userId: string) =>
      api.delete<SuccessResponseDto<OperationSuccessDto>>(
        `/groups/${groupId}/placeholders/${userId}`
      ),

    listPlaceholderClaims: (api: BaseApi, groupId: string) =>
      api.get<SuccessResponseDto<PlaceholderClaimListDto>>(
        `/groups/${groupId}/placeholders/claims`
      ),

    /**
     * Claim a placeholder from inside the group. The claimant is the
     * authenticated caller, so no id is sent.
     */
    requestPlaceholderClaim: (api: BaseApi, groupId: string, userId: string) =>
      api.post<SuccessResponseDto<PlaceholderClaimDto>, undefined>(
        `/groups/${groupId}/placeholders/${userId}/claims`,
        undefined
      ),

    confirmPlaceholderClaim: (
      api: BaseApi,
      groupId: string,
      userId: string,
      claimantId: string
    ) =>
      api.post<SuccessResponseDto<PlaceholderClaimDto>, undefined>(
        `/groups/${groupId}/placeholders/${userId}/claims/${claimantId}/confirm`,
        undefined
      ),

    /**
     * Set or clear the caller's single favorite group. Setting one clears
     * any previous favorite (enforced server-side). Per-user — only affects
     * the caller's own membership.
     */
    setFavorite: (api: BaseApi, groupId: string, isFavorite: boolean) =>
      api.put<SuccessResponseDto<void>, { isFavorite: boolean }>(
        `/groups/${groupId}/favorite`,
        { isFavorite }
      ),

    getSimplifiedDebts: (api: BaseApi, groupId: string, filterByUser = false) =>
      api.get<SuccessResponseDto<SimplifiedDebtsDto>>(
        `/groups/${groupId}/simplified-debts${
          filterByUser ? '?filterByUser=true' : ''
        }`
      ),
  };

  /**
   * Invitation endpoints
   */
  static invitations = {
    create: (api: BaseApi, groupId: string) =>
      api.post<SuccessResponseDto<InvitationResponseDto>, CreateInvitationDto>(
        `/groups/${groupId}/invitations/create`,
        { groupId }
      ),

    validate: (api: BaseApi, token: string) =>
      api.get<SuccessResponseDto<ValidateInvitationResponseDto>>(
        `/invitations/${token}/validate`
      ),

    accept: (api: BaseApi, token: string, claimPlaceholderId?: string) =>
      api.post<
        SuccessResponseDto<AcceptInvitationResponseDto>,
        AcceptInvitationDto
      >('/invitations/accept', {
        token,
        ...(claimPlaceholderId && { claimPlaceholderId }),
      }),

    delete: (api: BaseApi, invitationId: string) =>
      api.delete<SuccessResponseDto<void>>(`/invitations/${invitationId}`),
  };

  /**
   * A bill's public pick link: the token is the only credential.
   */
  static claimLinks = {
    view: (api: BaseApi, token: string) =>
      api.get<SuccessResponseDto<ClaimLinkViewDto>>(`/c/${token}`),

    /** `body.picks` is the person's full set afterwards; answers their bill after the pick; 410 once the link stopped. */
    pick: (api: BaseApi, token: string, body: LinkPickDto) =>
      api.put<SuccessResponseDto<LinkPickResultDto>, LinkPickDto>(
        `/c/${token}/picks`,
        body
      ),
  };

  /**
   * Expense endpoints
   */
  static expenses = {
    list: (
      api: BaseApi,
      groupId: string,
      params?: { page?: number; limit?: number }
    ) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<
        SuccessResponseDto<PaginatedResponseDto<ExpenseResponseDto>>
      >(`/expenses/group/${groupId}${queryString}`);
    },

    getById: (api: BaseApi, id: string) =>
      api.get<SuccessResponseDto<ExpenseResponseDto>>(`/expenses/${id}`),

    create: (api: BaseApi, data: CreateExpenseDto) =>
      api.post<SuccessResponseDto<ExpenseResponseDto>, CreateExpenseDto>(
        '/expenses',
        data
      ),

    update: (api: BaseApi, id: string, data: UpdateExpenseDto) =>
      api.put<SuccessResponseDto<ExpenseResponseDto>, UpdateExpenseDto>(
        `/expenses/${id}`,
        data
      ),

    delete: (api: BaseApi, id: string) =>
      api.delete<SuccessResponseDto<void>>(`/expenses/${id}`),

    /** `body.picks` is the caller's full set of units afterwards (see `claimPicksOf`); rows left out release their units. */
    claimLineItems: (
      api: BaseApi,
      expenseId: string,
      body: ClaimLineItemsDto
    ) =>
      api.put<SuccessResponseDto<ExpenseResponseDto>, ClaimLineItemsDto>(
        `/expenses/${expenseId}/line-items/claims`,
        body
      ),

    /** Within ten minutes of the last ask or reminder it fails with `errors:lineItems.remindTooSoon` (429). */
    remindPicks: (api: BaseApi, expenseId: string) =>
      api.post<SuccessResponseDto<{ reminded: number }>, object>(
        `/expenses/${expenseId}/picks/remind`,
        {}
      ),

    /** Answers the bill's one link, creating it on the first call. */
    createClaimLink: (api: BaseApi, expenseId: string) =>
      api.post<SuccessResponseDto<ClaimLinkDto>, object>(
        `/expenses/${expenseId}/claim-link`,
        {}
      ),

    deleteClaimLink: (api: BaseApi, expenseId: string) =>
      api.delete<SuccessResponseDto<void>>(`/expenses/${expenseId}/claim-link`),

    addComment: (api: BaseApi, expenseId: string, data: AddCommentDto) =>
      api.post<SuccessResponseDto<CommentResponseDto>, AddCommentDto>(
        `/expenses/${expenseId}/comments`,
        data
      ),

    updateComment: (
      api: BaseApi,
      commentId: string,
      data: UpdateCommentDto & { expenseId: string }
    ) =>
      api.put<SuccessResponseDto<CommentResponseDto>, UpdateCommentDto>(
        `/comments/${commentId}`,
        data
      ),

    deleteComment: (api: BaseApi, commentId: string, expenseId: string) =>
      api.delete<SuccessResponseDto<void>>(`/comments/${commentId}`, {
        expenseId,
      }),

    addReaction: (api: BaseApi, expenseId: string, reaction: string) =>
      api.post<SuccessResponseDto<void>, { reaction: string }>(
        `/expenses/${expenseId}/reactions`,
        { reaction }
      ),

    removeReaction: (api: BaseApi, expenseId: string, reaction: string) =>
      api.delete<SuccessResponseDto<void>, { reaction: string }>(
        `/expenses/${expenseId}/reactions`,
        { reaction }
      ),

    /**
     * Cloud-LLM category prediction (Claude Haiku, DynamoDB cache).
     * Mobile reaches this only when the on-device LLM isn't available
     * — see `apps/mobile/src/utils/categoryPrediction.ts`. Returns
     * the same `(category, subcategory, confidence)` shape the
     * native and keyword paths produce, plus a `source` discriminator
     * for diagnostics.
     */
    predictCategory: (api: BaseApi, params: PredictCategoryRequestDto) =>
      api.post<SuccessResponseDto<PredictCategoryResponseDto>>(
        '/expenses/predict-category',
        params
      ),

    /** A model answers, so this outlives the 15 s default. */
    describe: (api: BaseApi, body: DescribeExpenseRequestDto) =>
      api.post<
        SuccessResponseDto<DescribeExpenseResponseDto>,
        DescribeExpenseRequestDto
      >('/expenses/describe', body, {
        timeoutMs: VOICE_DESCRIBE_CLIENT_TIMEOUT_MS,
      }),
  };

  /**
   * Exchange rate endpoints
   */
  static exchangeRates = {
    get: (api: BaseApi, params: { from: string; to: string; date: string }) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<SuccessResponseDto<ExchangeRatePreviewDto>>(
        `/exchange-rates${queryString}`
      );
    },
  };

  /**
   * CSV import endpoints. The reviewed payload is large, so the POST is the
   * one write of the reviewed payload; progress is polled, and resume only
   * re-enters a failed or stalled job.
   */
  static imports = {
    createSplitwise: (api: BaseApi, body: SplitwiseImportRequestDto) =>
      api.post<
        SuccessResponseDto<CreateImportJobResponseDto>,
        SplitwiseImportRequestDto
      >('/imports/splitwise', body),

    getJob: (api: BaseApi, jobId: string) =>
      api.get<SuccessResponseDto<ImportJobDto>>(`/imports/${jobId}`),

    resumeJob: (api: BaseApi, jobId: string) =>
      api.post<SuccessResponseDto<CreateImportJobResponseDto>>(
        `/imports/${jobId}/resume`
      ),

    /** A model may answer, so this outlives the 15 s default. */
    resolveLabels: (api: BaseApi, body: ResolveSplitwiseLabelsRequestDto) =>
      api.post<
        SuccessResponseDto<ResolveSplitwiseLabelsResponseDto>,
        ResolveSplitwiseLabelsRequestDto
      >('/imports/splitwise/labels', body, { timeoutMs: 25_000 }),
  };

  /**
   * Balance endpoints
   */
  static balances = {
    getForGroup: (api: BaseApi, groupId: string) =>
      api.get<
        SuccessResponseDto<{
          balances: Record<string, Record<string, number>>;
        }>
      >(`/balance/group/${groupId}`),

    getForUser: (api: BaseApi) =>
      api.get<SuccessResponseDto<BalanceResponseDto[]>>('/balance/user'),

    getUserNet: (api: BaseApi) =>
      api.get<SuccessResponseDto<UserNetBalanceResponse>>('/balance/user/net'),
  };

  /**
   * Settlement endpoints
   */
  static settlements = {
    list: (
      api: BaseApi,
      groupId: string,
      params?: { page?: number; limit?: number; status?: SettlementStatus }
    ) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<
        SuccessResponseDto<{ settlements: SettlementResponseDto[] }>
      >(`/settlements/group/${groupId}${queryString}`);
    },

    create: (api: BaseApi, data: CreateSettlementDto) =>
      api.post<SuccessResponseDto<SettlementResponseDto>, CreateSettlementDto>(
        '/settlements',
        data
      ),

    update: (api: BaseApi, id: string, data: UpdateSettlementDto) =>
      api.put<SuccessResponseDto<SettlementResponseDto>, UpdateSettlementDto>(
        `/settlements/${id}`,
        data
      ),

    summary: (api: BaseApi, groupId: string) =>
      api.get<SuccessResponseDto<unknown>>(
        `/settlements/group/${groupId}/summary`
      ),
  };

  /**
   * Activity endpoints
   */
  static activity = {
    getFeed: (api: BaseApi, params?: { page?: number; limit?: number }) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<
        SuccessResponseDto<{
          activities: ActivityResponseDto[];
          hasMore: boolean;
        }>
      >(`/activity/feed${queryString}`);
    },
  };

  /**
   * Upload endpoints
   */
  static upload = {
    getPresignedUrl: (api: BaseApi, data: UploadPresignedUrlRequest) =>
      api.post<
        SuccessResponseDto<UploadPresignedUrlResponse>,
        UploadPresignedUrlRequest
      >('/upload/get-url', data),

    confirm: (api: BaseApi, data: UploadConfirmRequest) =>
      api.post<SuccessResponseDto<void>, UploadConfirmRequest>(
        '/upload/confirm',
        data
      ),
  };

  /**
   * Notification endpoints
   */
  static notifications = {
    list: (
      api: BaseApi,
      params?: {
        page?: number;
        limit?: number;
        unreadOnly?: boolean;
        type?: string;
        search?: string;
      }
    ) => {
      const queryString = ApiClientService.buildQueryString(params);
      return api.get<SuccessResponseDto<unknown>>(
        `/notifications${queryString}`
      );
    },

    /**
     * OPTIMIZED: Cursor-based pagination for infinite scroll
     * Use this for new implementations requiring high performance
     */
    listWithCursor: (
      api: BaseApi,
      params?: {
        limit?: number;
        cursor?: string;
        unreadOnly?: boolean;
        type?: string;
        search?: string;
      }
    ) => {
      const queryParams = { ...params, useCursor: true };
      const queryString = ApiClientService.buildQueryString(queryParams);
      return api.get<
        SuccessResponseDto<{
          items: import('@splitfiesta/shared').NotificationResponseDto[];
          nextCursor?: string;
          hasMore: boolean;
          unreadCount: number;
          unseenCount: number;
        }>
      >(`/notifications${queryString}`);
    },

    markAsRead: (
      api: BaseApi,
      notificationIds?: string[],
      markAll?: boolean,
      // `read` defaults to `true` so existing call sites (mark-as-read
      // flows) keep working unchanged. Pass `false` to flip the same
      // selection back to unread. `markAll: true` only supports
      // `read: true` server-side; passing `false` together is a 400.
      read = true
    ) => {
      if (markAll) {
        return api.put<SuccessResponseDto<unknown>>('/notifications/read', {
          markAll: true,
        });
      }
      return api.put<SuccessResponseDto<unknown>>('/notifications/read', {
        notificationIds,
        read,
      });
    },

    /**
     * Batch hard-delete endpoint. Pass `notificationIds` for one or
     * more specific items, or `deleteAll: true` to clear the
     * caller's entire feed. Single deletes are just the one-id
     * case — server batches via DynamoDB BatchWriteItem (25 / call)
     * so dismissing 200 notifications costs 8 DDB round-trips.
     */
    deleteNotifications: (
      api: BaseApi,
      params: { notificationIds?: string[]; deleteAll?: boolean }
    ) =>
      api.delete<
        SuccessResponseDto<{
          message: string;
          deletedCount?: number;
          timestamp: string;
        }>
      >('/notifications', params),

    /**
     * Mark all notifications as seen (clears badge count)
     * Called when user opens notification panel (Facebook-style UX)
     */
    markAsSeen: (api: BaseApi) =>
      api.put<SuccessResponseDto<void>>('/notifications/seen', {}),

    getSettings: (api: BaseApi) =>
      api.get<SuccessResponseDto<NotificationSettingsResponseDto>>(
        '/notifications/settings'
      ),

    updateSettings: (api: BaseApi, settings: UpdateNotificationSettingsDto) =>
      api.put<SuccessResponseDto<NotificationSettingsResponseDto>>(
        '/notifications/settings',
        settings
      ),

    /**
     * Register a device's raw APNs/FCM push token so the API can deliver
     * push via SNS. Called on login and whenever the OS rotates the token.
     */
    subscribePush: (api: BaseApi, body: SubscribeDeviceDto) =>
      api.post<SuccessResponseDto<unknown>>('/notifications/subscribe', body),

    /** Remove a device's push registration (called on logout). */
    unsubscribePush: (api: BaseApi, body: { deviceToken: string }) =>
      api.delete<SuccessResponseDto<unknown>>('/notifications/subscribe', body),
  };

  // Admin endpoints
  static admin = {
    startShadow: (api: BaseApi, body: StartShadowDto) =>
      api.post<SuccessResponseDto<ShadowTokenResponseDto>>(
        '/admin/shadow',
        body
      ),

    endShadow: (api: BaseApi, body: { targetUserId: string }) =>
      api.post<SuccessResponseDto<{ message: string }>>(
        '/admin/shadow/end',
        body
      ),

    searchUsers: (api: BaseApi, query: string) =>
      api.get<SuccessResponseDto<AdminUserSearchResultDto[]>>(
        `/admin/users/search?q=${encodeURIComponent(query)}`
      ),
  };
}

export const apiClientService = new ApiClientService();
